import { useToken } from '@/common/helper';
import CustomButton from '@/components/CustomButton.jsx';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import { useSpin } from '@/components/SpinContext.jsx';
import { Col, Flex, Form, Input, message, Row, TimePicker } from 'antd';
import { isValidPhoneNumber } from 'libphonenumber-js';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { AddAirportContact, DeleteAirportContact } from '@/apis/airports.js';
import { DeleteIcon } from '@/assets/icons/icons';

const { TextArea } = Input;

// ---------------------- TYPES ----------------------

export interface Airport {
  iata_code: string;
}

export interface Airline {
  name: string;
}

export interface ContactRecord {
  id?: string;
  role: string;
  name: string;
  email: string;
  phone_number?: string;
  notes?: string;
  start_time?: number | null;
  end_time?: number | null;

  created_by?: number;
  updated_by?: number;

  airport_name?: string;
  airline_name?: string;
}

interface airportPermissionsProps {
  is_update?: boolean;
  is_delete?: boolean;
}

export interface ContactFormProps {
  airportPermissions?: airportPermissionsProps;
  selelctedContact?: ContactRecord | null;
  drawerOpen: boolean;
  onClose: () => void;
  unauthorize: (e: unknown) => void;
  refreshList: () => void;
  airport: Airport;
  airline: Airline;
}

// Form values type
interface ContactFormValues {
  role: string;
  name: string;
  email: string;
  phone_number?: string;
  notes?: string;
  start_time: Dayjs | null;
  end_time: Dayjs | null;
}

// ----------------------------------------------------

const ContactForm = ({
  airportPermissions,
  selelctedContact,
  drawerOpen,
  onClose,
  unauthorize,
  refreshList,
  airport,
  airline,
}: ContactFormProps) => {
  const [form] = Form.useForm<ContactFormValues>();
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const isEditing = !!selelctedContact;

  // ---------------------- UTIL ----------------------
  const parseToTimestamp = (time: Dayjs | null): number | null => {
    if (!time) return null;
    return dayjs(time).valueOf();
  };

  const buildPayload = (values: ContactFormValues): ContactRecord => {
    const start = parseToTimestamp(values.start_time);
    const end = parseToTimestamp(values.end_time);

    return {
      id: selelctedContact?.id,
      role: values.role.trim(),
      name: values.name.trim(),
      email: values.email.trim(),
      phone_number: values.phone_number || '',
      notes: values.notes || '',
      start_time: start,
      end_time: end,
      airport_name: airport?.iata_code,
      airline_name: airline.name,
      created_by: selelctedContact?.created_by,
    };
  };

  // ---------------------- API ----------------------
  const saveContactToServer = async (contact: ContactRecord) => {
    try {
      showSpin();
      const response = await AddAirportContact(contact, token);

      if (response?.data?.data?.is_success) {
        message.success(response?.data?.data?.message);
        refreshList();
        onClose();
      } else {
        message.error(response?.data?.data?.message || 'Request failed');
      }
    } catch (error) {
      unauthorize(error);
      message.error('Failed to fetch contacts');
    } finally {
      hideSpin();
    }
  };

  // ---------------------- DEFAULT VALUES ----------------------
  useEffect(() => {
    if (selelctedContact && drawerOpen) {
      form.setFieldsValue({
        role: selelctedContact.role || '',
        name: selelctedContact.name || '',
        email: selelctedContact.email || '',
        phone_number: selelctedContact.phone_number || '',
        notes: selelctedContact.notes || '',
        start_time: selelctedContact.start_time
          ? dayjs(selelctedContact.start_time)
          : null,
        end_time: selelctedContact.end_time
          ? dayjs(selelctedContact.end_time)
          : null,
      });
    } else {
      form.resetFields();
    }
  }, [selelctedContact, drawerOpen, form]);

  // ---------------------- SUBMIT ----------------------
  const onFinish = async (values: ContactFormValues) => {
    try {
      await form.validateFields();
      const payload = buildPayload(values);
      await saveContactToServer(payload);
    } catch {
      message.error('Please complete required fields');
    }
  };

  const deleteContactFromServer = async () => {
    try {
      showSpin();
      const response = await DeleteAirportContact(selelctedContact?.id, token);
      if (response?.data?.data?.is_success) {
        message.success(response?.data?.data?.message);
        refreshList();
        onClose();
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to delete contact'
        );
      }
    } catch (error) {
      unauthorize(error);
      message.error('Failed to delete contact');
    } finally {
      hideSpin();
    }
  };

  // ---------------------- UI ----------------------
  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      {/* Header */}
      {airportPermissions?.is_delete && isEditing && (
        <div className='contact-form-header-delete'>
          <span
            className={`arrow-icon h-4 `}
            onClick={() => deleteContactFromServer()}
          >
            <DeleteIcon />
          </span>
        </div>
      )}
      <div className='contact-form-header-title'>
        <CustomTitle
          text={isEditing ? 'Edit contact' : 'Add contact'}
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      {/* Form Fields */}
      <div className='custom-scrollbar contact-form-scrollable'>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label='Role'
              name='role'
              rules={[
                { required: true, message: 'Please enter role' },
                { max: 20, message: 'Max 20 characters allowed' },
              ]}
            >
              <CustomInput
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-primary)'
                borderRadius='8px'
                border='none'
                placeholderColor='var(--text-secondary)'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Name'
              name='name'
              rules={[
                { required: true, message: 'Please enter name' },
                {
                  pattern:
                    /^(?=.*[A-Za-zÀ-ÖØ-öø-ÿ])[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.'\-()]{2,100}$/,
                  message:
                    'Name must include at least one letter and only valid characters',
                },
                { min: 3, message: 'Must be at least 3 characters' },
                { max: 20, message: 'Max 20 characters allowed' },
              ]}
            >
              <CustomInput
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-primary)'
                borderRadius='8px'
                border='none'
                placeholderColor='var(--text-secondary)'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label='Shift hours'>
              <Flex gap={8} align='center'>
                <Form.Item name='start_time' noStyle>
                  <TimePicker
                    use12Hours
                    format='h:mm A'
                    placeholder='00:00 AM'
                    className='contact-form-timepicker'
                  />
                </Form.Item>
                <span>-</span>
                <Form.Item name='end_time' noStyle>
                  <TimePicker
                    use12Hours
                    format='h:mm A'
                    placeholder='00:00 PM'
                    className='contact-form-timepicker'
                  />
                </Form.Item>
              </Flex>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Email'
              name='email'
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <CustomInput
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-secondary)'
                borderRadius='4px'
                border='none'
                placeholder='Email'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Phone number'
              name='phone_number'
              rules={[
                { required: true, message: 'Please enter phone number' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    return isValidPhoneNumber(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error('Invalid phone number'));
                  },
                },
              ]}
            >
              <PhoneInput
                country='us'
                value={form.getFieldValue('phone_number')}
                onChange={(_, __, ___, formattedValue) =>
                  form.setFieldsValue({ phone_number: formattedValue })
                }
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Notes'
              name='notes'
              rules={[
                { required: true, message: 'Please enter notes' },
                { max: 300, message: 'Max 300 characters allowed' },
              ]}
            >
              <TextArea
                rows={2}
                style={{ resize: 'none' }}
                className='contact-form-textarea'
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <div className='contact-form-footer'>
        <Row gutter={[0, 8]}>
          <CustomButton
            text='Save'
            bgColor='#000'
            htmlType='submit'
            color='#fff'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
            width='100%'
            borderRadius='8px'
            isFormValid={false}
          />
          <CustomButton
            text='Cancel'
            bgColor='transparent'
            color='var(--text-primary)'
            fontSize='16px'
            padding='11.5px 0px 12px 0px'
            fontWeight='600'
            boxShadow='none'
            height='auto'
            width='100%'
            border='none'
            onClick={onClose}
          />
        </Row>
      </div>
    </Form>
  );
};

export default ContactForm;
